import { useState } from "react";
import { z } from "zod";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const schema = z.object({
  name: z.string().trim().min(1, "Add your name").max(80, "Keep it under 80 characters"),
  title: z.string().trim().min(1, "Add your title").max(80, "Keep it under 80 characters"),
  firm: z.string().trim().max(80, "Keep it under 80 characters").optional(),
  city: z.string().trim().max(80, "Keep it under 80 characters").optional(),
  email: z.string().trim().email("That doesn't look like an email").max(255),
});

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function RequestAccessDialog({ open, onOpenChange }: Props) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [firm, setFirm] = useState("");
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function reset() {
    setName("");
    setTitle("");
    setFirm("");
    setCity("");
    setEmail("");
    setError(null);
    setDone(false);
    setSubmitting(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ name, title, firm, city, email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check your details");
      return;
    }
    setSubmitting(true);
    // Firm and city ride along in the title field so reviewers see the full
    // picture in one line: "Partner · Evercore · New York".
    const fullTitle = [parsed.data.title, parsed.data.firm, parsed.data.city]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(" · ")
      .slice(0, 200);
    // Goes through a public server route so the "You're on the list" email
    // can be queued with server credentials in the same step.
    try {
      const res = await fetch("/api/public/request-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: parsed.data.name,
          title: fullTitle,
          email: parsed.data.email,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
          referrer:
            typeof document !== "undefined" ? (document.referrer || null)?.slice(0, 500) : null,
        }),
      });
      setSubmitting(false);
      if (!res.ok) {
        setError("Something went wrong. Try again in a moment.");
        return;
      }
    } catch {
      setSubmitting(false);
      setError("Something went wrong. Try again in a moment.");
      return;
    }
    setDone(true);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent className="max-w-sm">
        {done ? (
          <div className="space-y-4 py-2 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-center">Your application is in</DialogTitle>
              <DialogDescription className="text-center">
                Every application is reviewed personally. Invitations open in small groups, and we'll email you when yours is ready.
              </DialogDescription>
            </DialogHeader>
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-2">
              <DialogTitle>Apply for Pyng</DialogTitle>
              <DialogDescription>
                Pyng is a private network built around relationship quality. Tell us a little about yourself; every application is reviewed personally.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ra-name">Name</Label>
                <Input
                  id="ra-name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  maxLength={80}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ra-title">Title</Label>
                <Input
                  id="ra-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Managing Partner"
                  maxLength={80}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ra-firm">
                    Firm <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="ra-firm"
                    value={firm}
                    onChange={(e) => setFirm(e.target.value)}
                    placeholder="Acme Partners"
                    maxLength={80}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ra-city">
                    City <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="ra-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="New York"
                    maxLength={80}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ra-email">Email</Label>
                <Input
                  id="ra-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                  maxLength={255}
                  required
                />
              </div>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Submit application
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                No spam. Just one email when your invitation is ready.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}